// apps/share-web/src/chart/local-clock.ts
//
// «Ведение по темпу» (words page v2): локальные часы вместо лидера. У песни уже
// есть расписание секций в секундах (bpm × такты — то же, чем ведёт лайв), а
// автоскролл с ручной скоростью не подобрать никогда (Сергей 14.09). Часы дают
// `PositionSource` для того же chart-follow: лист и лента идут по расписанию
// сами, без подключения.
//
// Часы ВЫВОДЯТ позицию (`начало + (сейчас − старт)`), не накапливают: rAF в
// фоновой вкладке замирает, а вернувшись, картинка обязана встать на место
// сама — то же правило 2, что у часовой модели лайва (live-model.ts).
//
// Конец: дошли до `endSec` — стоим на нём с `playing:false`. Экстраполировать за
// конец песни значит врать (правило 5 там же).

import type { PositionSource } from './chart-follow.js';

export interface LocalClock extends PositionSource {
  /** Заново для песни: позиция 0, пауза. `endSec` — конец расписания (null = без потолка). */
  arm(songIdx: number, endSec: number | null): void;
  start(): void;
  pause(): void;
  /** Поставить позицию (тап по чипу секции), не меняя «играет/пауза». */
  seek(posSec: number): void;
  isPlaying(): boolean;
  posSec(): number;
  /** Для какой песни заведены часы; −1 — ни для какой. */
  songIdx(): number;
}

export function createLocalClock({ now = () => Date.now() }: { now?: () => number } = {}): LocalClock {
  let song = -1;
  let end: number | null = null;
  let base = 0;                 // позиция на момент старта/seek, сек
  let startedAt: number | null = null;   // локальные мс; null = пауза

  const pos = (): number => {
    let p = startedAt == null ? base : base + (now() - startedAt) / 1000;
    if (end != null && p >= end) {
      p = end;
      // Дошли — встали. Без этого следующий кадр считал бы дальше конца.
      base = end; startedAt = null;
    }
    return Math.max(0, p);
  };

  return {
    arm(songIdx, endSec) { song = songIdx; end = endSec != null && endSec > 0 ? endSec : null; base = 0; startedAt = null; },
    start() { if (song < 0) return; if (end != null && base >= end) base = 0; if (startedAt == null) startedAt = now(); },
    pause() { if (startedAt == null) return; base = pos(); startedAt = null; },
    seek(posSec) {
      const p = Math.max(0, Number.isFinite(posSec) ? posSec : 0);
      base = end != null ? Math.min(p, end) : p;
      if (startedAt != null) startedAt = now();
    },
    isPlaying: () => { pos(); return startedAt != null; },
    posSec: () => pos(),
    songIdx: () => song,
    now() {
      if (song < 0) return null;
      const posSec = pos();
      return { songIdx: song, posSec, playing: startedAt != null };
    },
  };
}

/** «0:42» для подписи пилюли. */
export function fmtClock(sec: number): string {
  const s = Math.max(0, Math.floor(sec));
  return `${Math.floor(s / 60)}:${String(s % 60).padStart(2, '0')}`;
}
